// Campos de un producto suelto, compartidos por el alta y la edición.
import { useId, type ReactNode } from 'react';
import {
    Controller,
    useController,
    useWatch,
    type Control,
    type UseControllerReturn,
} from 'react-hook-form';
import { Input } from '@/kit';
import { Textarea } from '@/components/ui/textarea';
import {
    ACCESSORY_SUBCATEGORY_OPTIONS,
    MDM_LOCK_MODE_OPTIONS,
    MDM_PROVIDER_OPTIONS,
    PRODUCT_CATEGORY_OPTIONS,
    TIER_FORM_OPTIONS,
    productGridFieldsOf,
    type EditForm,
    type ProductGridField,
} from '../../lib/catalogForms';
import { FinancierChecks, FormSection, FormSelect, FormSwitch } from './FormBits';

/** Lo que el alta y la edición de producto tienen en común. */
export type ProductFieldValues = Omit<EditForm, 'components'>;

type TextName =
    | 'brand'
    | 'model'
    | 'capacity'
    | 'color'
    | 'description'
    | 'imageUrl'
    | 'externalId'
    | 'salePriority'
    | 'warrantyMonths'
    | 'launchDate';

export function TextController({
    control,
    name,
    label,
    placeholder,
    hint,
    required,
    type,
    inputMode,
}: {
    control: Control<ProductFieldValues>;
    name: TextName;
    label: string;
    placeholder?: string;
    hint?: string;
    required?: boolean;
    type?: 'date';
    inputMode?: 'numeric';
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <Input
                    label={label}
                    placeholder={placeholder}
                    hint={hint}
                    required={required}
                    type={type}
                    inputMode={inputMode}
                    error={fieldState.error?.message}
                    {...field}
                />
            )}
        />
    );
}

/** Texto de varios renglones con etiqueta, ayuda y error, al estilo del `Input` del kit. */
function LinesController({
    control,
    name,
    label,
    placeholder,
    hint,
}: {
    control: Control<ProductFieldValues>;
    name: 'highlightsText' | 'specsText';
    label: string;
    placeholder: string;
    hint: string;
}) {
    const id = useId();
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <div className="space-y-1.5">
                    <label htmlFor={id} className="text-sm font-medium text-foreground">
                        {label}
                    </label>
                    <Textarea
                        id={id}
                        rows={3}
                        placeholder={placeholder}
                        aria-invalid={fieldState.error ? true : undefined}
                        {...field}
                    />
                    {fieldState.error ? (
                        <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    ) : (
                        <p className="text-xs text-muted-foreground">{hint}</p>
                    )}
                </div>
            )}
        />
    );
}

/** Prioridad en «Recomendados» y ficha comercial que ve el vendedor. */
function CommercialFields({ control }: { control: Control<ProductFieldValues> }) {
    return (
        <FormSection title="Ficha comercial">
            <div className="grid grid-cols-3 gap-3">
                <TextController
                    control={control}
                    name="salePriority"
                    label="Prioridad de venta"
                    inputMode="numeric"
                    placeholder="0 a 100"
                    hint="100 sale primero en «Recomendados»."
                />
                <TextController
                    control={control}
                    name="warrantyMonths"
                    label="Garantía (meses)"
                    inputMode="numeric"
                    hint="0 es sin garantía."
                />
                <TextController
                    control={control}
                    name="launchDate"
                    label="Fecha de lanzamiento"
                    type="date"
                />
            </div>
            <LinesController
                control={control}
                name="highlightsText"
                label="Puntos destacados"
                placeholder={'Cámara de 50 MP\nBatería de 5000 mAh'}
                hint="Uno por renglón. Déjalo vacío para quitarlos."
            />
            <LinesController
                control={control}
                name="specsText"
                label="Ficha técnica"
                placeholder={'Pantalla: 6.5 pulgadas\nProcesador: Octa-core'}
                hint="Un dato por renglón como «clave: valor». Déjala vacía para quitarla."
            />
        </FormSection>
    );
}

/** Interruptores de venta, compartidos por producto, kit y servicio. */
export function SaleSwitches({ control }: { control: Control<ProductFieldValues> }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <Controller
                control={control}
                name="sellable"
                render={({ field }) => (
                    <FormSwitch
                        label="Se vende suelto"
                        hint="Apagado, solo se vende dentro de un kit."
                        checked={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name="showInSalesFlow"
                render={({ field }) => (
                    <FormSwitch
                        label="Aparece en el flujo de venta"
                        hint="Lo ve el vendedor en el wizard."
                        checked={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
        </div>
    );
}

/** Un campo por celda; si el total es impar, el último va a lo ancho para no dejar hueco. */
function gridCellClass(index: number, count: number): string | undefined {
    return count % 2 === 1 && index === count - 1 ? 'col-span-2' : undefined;
}

function MdmFields({
    mdm,
    provider,
    lockMode,
}: {
    mdm: UseControllerReturn<ProductFieldValues, 'mdmEnabled'>;
    provider: UseControllerReturn<ProductFieldValues, 'mdmProvider'>;
    lockMode: UseControllerReturn<ProductFieldValues, 'mdmLockMode'>;
}) {
    return (
        <>
            <FormSwitch
                label="Bloqueo MDM"
                hint="El equipo se enrola para bloquearse si el crédito cae en mora."
                checked={mdm.field.value}
                onChange={mdm.field.onChange}
            />
            {mdm.field.value ? (
                <div className="grid grid-cols-2 gap-3">
                    <FormSelect
                        label="Proveedor MDM"
                        required
                        value={provider.field.value}
                        onChange={provider.field.onChange}
                        options={MDM_PROVIDER_OPTIONS}
                        placeholder="Elige el proveedor"
                        error={provider.fieldState.error?.message}
                    />
                    <FormSelect
                        label="Modo de bloqueo"
                        required
                        value={lockMode.field.value}
                        onChange={lockMode.field.onChange}
                        options={MDM_LOCK_MODE_OPTIONS}
                        placeholder="Elige el modo"
                        error={lockMode.fieldState.error?.message}
                    />
                </div>
            ) : null}
        </>
    );
}

function TierField({
    control,
    allowNoTier,
}: {
    control: Control<ProductFieldValues>;
    allowNoTier: boolean;
}) {
    return (
        <Controller
            control={control}
            name="tier"
            render={({ field }) => (
                <FormSelect
                    label="Gama"
                    value={field.value}
                    onChange={field.onChange}
                    options={
                        allowNoTier
                            ? TIER_FORM_OPTIONS
                            : TIER_FORM_OPTIONS.filter((o) => o.value !== '')
                    }
                    placeholder="Elige la gama"
                />
            )}
        />
    );
}

/** Lo que Fiado edita de un producto de cadena integrada: gama, financieras y MDM. */
export function IntegratedProductFields({
    control,
    leading,
}: {
    control: Control<ProductFieldValues>;
    leading?: ReactNode;
}) {
    const category = useWatch({ control, name: 'category' });
    const mdm = useController({ control, name: 'mdmEnabled' });
    const provider = useController({ control, name: 'mdmProvider' });
    const lockMode = useController({ control, name: 'mdmLockMode' });
    return (
        <FormSection title="Venta">
            {leading}
            <TierField control={control} allowNoTier={false} />
            <Controller
                control={control}
                name="eligibleFinanciers"
                render={({ field }) => (
                    <FinancierChecks value={field.value} onChange={field.onChange} />
                )}
            />
            {category === 'PHONE' ? (
                <MdmFields mdm={mdm} provider={provider} lockMode={lockMode} />
            ) : null}
        </FormSection>
    );
}

export function ProductFields({
    control,
    leading,
    categoryLocked = false,
    allowNoTier = true,
}: {
    control: Control<ProductFieldValues>;
    /** Lo que va arriba de la sección «Producto»: el SKU y el rastreo en el alta, el aviso en la edición. */
    leading?: ReactNode;
    categoryLocked?: boolean;
    /** En edición no se ofrece «Sin gama»: el PATCH no admite vaciar la gama. */
    allowNoTier?: boolean;
}) {
    const category = useWatch({ control, name: 'category' });
    const mdm = useController({ control, name: 'mdmEnabled' });
    const provider = useController({ control, name: 'mdmProvider' });
    const lockMode = useController({ control, name: 'mdmLockMode' });
    const subcategory = useController({ control, name: 'accessorySubcategory' });
    // Solo un accesorio lleva subcategoría y solo un teléfono lleva MDM.
    const onCategoryChange = (next: string) => {
        if (next !== 'ACCESSORY') subcategory.field.onChange('');
        if (next === 'PHONE') return;
        mdm.field.onChange(false);
        provider.field.onChange('');
        lockMode.field.onChange('');
    };
    const cells: Record<ProductGridField, ReactNode> = {
        category: (
            <Controller
                control={control}
                name="category"
                render={({ field, fieldState }) => (
                    <FormSelect
                        label="Categoría"
                        required
                        value={field.value}
                        onChange={(next) => {
                            field.onChange(next);
                            onCategoryChange(next);
                        }}
                        options={PRODUCT_CATEGORY_OPTIONS}
                        placeholder="Elige la categoría"
                        error={fieldState.error?.message}
                        disabled={categoryLocked}
                    />
                )}
            />
        ),
        tier: <TierField control={control} allowNoTier={allowNoTier} />,
        accessorySubcategory: (
            <FormSelect
                label="Subcategoría"
                value={subcategory.field.value}
                onChange={subcategory.field.onChange}
                options={ACCESSORY_SUBCATEGORY_OPTIONS}
                placeholder="Elige la subcategoría"
                error={subcategory.fieldState.error?.message}
            />
        ),
        brand: <TextController control={control} name="brand" label="Marca" required />,
        model: <TextController control={control} name="model" label="Modelo" required />,
        capacity: (
            <TextController
                control={control}
                name="capacity"
                label="Capacidad"
                placeholder="128GB"
            />
        ),
        color: <TextController control={control} name="color" label="Color" />,
    };
    const fields = productGridFieldsOf(category);
    return (
        <>
            <FormSection title="Producto">
                {leading}
                <div className="grid grid-cols-2 gap-3">
                    {fields.map((name, index) => (
                        <div key={name} className={gridCellClass(index, fields.length)}>
                            {cells[name]}
                        </div>
                    ))}
                </div>
                <TextController control={control} name="description" label="Descripción" />
                <div className="grid grid-cols-2 gap-3">
                    <TextController
                        control={control}
                        name="externalId"
                        label="ID externo"
                        hint="El SKU en el sistema del retailer (Zentral)."
                    />
                    <TextController
                        control={control}
                        name="imageUrl"
                        label="URL de imagen"
                        placeholder="https://"
                    />
                </div>
            </FormSection>
            <FormSection title="Venta">
                <SaleSwitches control={control} />
                <Controller
                    control={control}
                    name="eligibleFinanciers"
                    render={({ field }) => (
                        <FinancierChecks value={field.value} onChange={field.onChange} />
                    )}
                />
                {category === 'PHONE' ? (
                    <MdmFields mdm={mdm} provider={provider} lockMode={lockMode} />
                ) : null}
            </FormSection>
            <CommercialFields control={control} />
        </>
    );
}
